import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import CloseButton from "components/layout/CloseButton";
import FunctionalButton from "components/layout/FunctionalButton";
import BottomSheet from "components/layout/motion/BottomSheet";
import {
    fetchStcmFilesList,
    getStcmMapListBaseUrl,
    getStcmPreviewBmpUrl,
    stcmDisplayName,
} from "lib/consts/stcmMapList";
import {
    fetchRecordings,
    getRecorderBaseUrl,
    getRecordingFileUrl,
} from "lib/recorderApi";
import {
    sendTelegramDocument,
    sendTelegramMessage,
    sendTelegramPhoto,
    sendTelegramVideo,
    TELEGRAM_TEST_MESSAGE,
} from "lib/telegramApi";
import { useARC } from "providers/ARC/ARCProvider";
import { authedFetch } from "lib/utils/authedFetch";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useMap } from "providers/navigation/MapProvider";
import { useToast } from "providers/ToastProvider";

import type { RecordingFileInfo } from "lib/recorderApi";
import {
    formatBytes,
    isPhotoFile as isGalleryPhotoFilename,
    isVideoFile as isGalleryVideoFilename,
} from "components/control/recordingsMediaUtils";

const STORAGE_KEY = "device_settings_telegram_v1";

function interpretToggle(value: unknown, defaultOn: boolean): boolean {
    return typeof value === "boolean" ? value : defaultOn;
}

/** Official R2C2 notification bot — users must start a chat with this bot before messages can be delivered. */
const NOTIFICATION_BOT_USERNAME = "testnotipusharcbot";
const NOTIFICATION_BOT_TELEGRAM_URL = `https://t.me/${NOTIFICATION_BOT_USERNAME}`;

type TelegramSettings = {
    enabled: boolean;
    botToken: string;
    chatId: string;
    notifyPerson: boolean;
    notifyFire: boolean;
};

function loadStored(): TelegramSettings {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return {
                enabled: false,
                botToken: "",
                chatId: "",
                notifyPerson: true,
                notifyFire: true,
            };
        }
        const parsed = JSON.parse(raw) as Partial<TelegramSettings>;
        return {
            enabled: Boolean(parsed.enabled),
            botToken: typeof parsed.botToken === "string" ? parsed.botToken : "",
            chatId: typeof parsed.chatId === "string" ? parsed.chatId : "",
            notifyPerson: interpretToggle(parsed.notifyPerson, true),
            notifyFire: interpretToggle(parsed.notifyFire, true),
        };
    } catch {
        return {
            enabled: false,
            botToken: "",
            chatId: "",
            notifyPerson: true,
            notifyFire: true,
        };
    }
}

function persist(settings: TelegramSettings) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

type MediaSendKind = "map" | "photo" | "video" | null;
type MapSource = "live" | "saved";
type GalleryKind = "photo" | "video";

const TelegramNotificationSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const mapCtx = useMap();
    const mapImageUrl = mapCtx?.mapImageUrl ?? null;

    const stcmBaseUrl = useMemo(
        () => getStcmMapListBaseUrl(selectedARC?.host, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );
    const recorderBaseUrl = useMemo(
        () => getRecorderBaseUrl(selectedARC?.host, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [enabled, setEnabled] = useState(false);
    const [botToken, setBotToken] = useState("");
    const [chatId, setChatId] = useState("");
    const [notifyPerson, setNotifyPerson] = useState(true);
    const [notifyFire, setNotifyFire] = useState(true);
    const [hydrated, setHydrated] = useState(false);
    const [testBusy, setTestBusy] = useState(false);
    const [mediaBusy, setMediaBusy] = useState<MediaSendKind>(null);

    const [mapSource, setMapSource] = useState<MapSource>("live");
    const [savedStcmRel, setSavedStcmRel] = useState("");
    const [stcmFiles, setStcmFiles] = useState<string[]>([]);
    const [stcmListLoading, setStcmListLoading] = useState(false);
    const [stcmListError, setStcmListError] = useState<string | null>(null);
    const [mapPickerOpen, setMapPickerOpen] = useState(false);

    const [galleryKind, setGalleryKind] = useState<GalleryKind | null>(null);
    const [galleryFiles, setGalleryFiles] = useState<RecordingFileInfo[]>([]);
    const [galleryLoading, setGalleryLoading] = useState(false);
    const [galleryError, setGalleryError] = useState<string | null>(null);
    /** Filename selected in the gallery modal (preview + Send). */
    const [gallerySelectedName, setGallerySelectedName] = useState<string | null>(null);

    useEffect(() => {
        const s = loadStored();
        setEnabled(s.enabled);
        setBotToken(s.botToken);
        setChatId(s.chatId);
        setNotifyPerson(s.notifyPerson);
        setNotifyFire(s.notifyFire);
        setHydrated(true);
    }, []);

    useEffect(() => {
        if (!mapPickerOpen || !stcmBaseUrl) {
            if (!mapPickerOpen) {
                setStcmListLoading(false);
            }
            return;
        }
        let cancelled = false;
        setStcmListLoading(true);
        setStcmListError(null);
        fetchStcmFilesList(stcmBaseUrl)
            .then((data) => {
                if (cancelled) return;
                const stcmOnly = (data.files ?? []).filter((f) =>
                    f.toLowerCase().endsWith(".stcm")
                );
                setStcmFiles(stcmOnly);
            })
            .catch((e) => {
                if (cancelled) return;
                setStcmFiles([]);
                setStcmListError(toUserFacingApiError(e, t("telegram.loadSavedMapsFailed")));
            })
            .finally(() => {
                if (!cancelled) setStcmListLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [mapPickerOpen, stcmBaseUrl]);

    useEffect(() => {
        if (!savedStcmRel || !stcmFiles.includes(savedStcmRel)) {
            setSavedStcmRel(stcmFiles[0] ?? "");
        }
    }, [stcmFiles, savedStcmRel]);

    const refreshGalleryList = useCallback(async () => {
        if (!recorderBaseUrl) {
            setGalleryFiles([]);
            setGalleryError(
                t("telegram.recorderUrlNotConfigured")
            );
            return;
        }
        setGalleryLoading(true);
        setGalleryError(null);
        try {
            const res = await fetchRecordings(recorderBaseUrl);
            setGalleryFiles(res.files ?? []);
        } catch (e) {
            setGalleryFiles([]);
            setGalleryError(toUserFacingApiError(e, t("telegram.loadGalleryFailed")));
        } finally {
            setGalleryLoading(false);
        }
    }, [recorderBaseUrl, t]);

    useEffect(() => {
        if (galleryKind) {
            void refreshGalleryList();
        }
    }, [galleryKind, refreshGalleryList]);

    const filteredGalleryFiles = useMemo(() => {
        if (!galleryKind) return [];
        return galleryFiles.filter((f) =>
            galleryKind === "photo"
                ? isGalleryPhotoFilename(f.name)
                : isGalleryVideoFilename(f.name)
        );
    }, [galleryFiles, galleryKind]);

    useEffect(() => {
        if (!galleryKind || filteredGalleryFiles.length === 0) {
            setGallerySelectedName(null);
            return;
        }
        setGallerySelectedName((prev) => {
            if (prev && filteredGalleryFiles.some((f) => f.name === prev)) {
                return prev;
            }
            return filteredGalleryFiles[0]?.name ?? null;
        });
    }, [galleryKind, filteredGalleryFiles]);

    const gallerySelectedUrl = useMemo(() => {
        if (!recorderBaseUrl || !gallerySelectedName) return null;
        return getRecordingFileUrl(recorderBaseUrl, gallerySelectedName);
    }, [recorderBaseUrl, gallerySelectedName]);

    const handleSave = useCallback(() => {
        const next: TelegramSettings = {
            enabled,
            botToken: botToken.trim(),
            chatId: chatId.trim(),
            notifyPerson,
            notifyFire,
        };
        persist(next);
        addToast({ type: "success", message: t("telegram.saved") });
    }, [addToast, botToken, chatId, enabled, notifyFire, notifyPerson, t]);

    const handleTest = useCallback(async () => {
        const token = botToken.trim();
        const chat = chatId.trim();
        if (!token || !chat) {
            addToast({
                type: "error",
                message: t("telegram.enterCredentialsTest"),
            });
            return;
        }
        setTestBusy(true);
        try {
            await sendTelegramMessage(token, chat, TELEGRAM_TEST_MESSAGE);
            addToast({ type: "success", message: t("telegram.testSent") });
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("telegram.testFailed")),
            });
        } finally {
            setTestBusy(false);
        }
    }, [addToast, botToken, chatId, t]);

    const credentialsReady = Boolean(botToken.trim() && chatId.trim());
    const anyBusy = testBusy || mediaBusy !== null;

    const canSendLiveMap = mapSource === "live" && Boolean(mapImageUrl);
    const canSendSavedMap =
        mapSource === "saved" &&
        Boolean(stcmBaseUrl && savedStcmRel && !stcmListLoading && !stcmListError);
    const canSendMap = mapSource === "live" ? canSendLiveMap : canSendSavedMap;

    const showLiveMapPreview = mapSource === "live" && Boolean(mapImageUrl);
    /** Preview image may load while the saved list is still fetching. */
    const showSavedMapPreview =
        mapSource === "saved" && Boolean(stcmBaseUrl && savedStcmRel && !stcmListError);
    const savedMapPreviewUrl =
        showSavedMapPreview && stcmBaseUrl && savedStcmRel
            ? getStcmPreviewBmpUrl(stcmBaseUrl, savedStcmRel)
            : null;

    const handleSendMap = useCallback(async () => {
        const token = botToken.trim();
        const chat = chatId.trim();
        if (!token || !chat) {
            addToast({
                type: "error",
                message: t("telegram.enterCredentials"),
            });
            return;
        }

        if (mapSource === "live") {
            if (!mapImageUrl) {
                addToast({
                    type: "error",
                    message: t("telegram.noMapImage"),
                });
                return;
            }
            setMediaBusy("map");
            try {
                const res = await fetch(mapImageUrl);
                if (!res.ok) {
                    throw new Error("Couldn't read the map image. Try again.");
                }
                const blob = await res.blob();
                const isPng =
                    blob.type === "image/png" || mapImageUrl.toLowerCase().includes("png");
                const filename = `arc-map-${Date.now()}.${isPng ? "png" : "jpg"}`;
                await sendTelegramDocument(token, chat, blob, {
                    filename,
                    caption: "ARC live map",
                });
                addToast({ type: "success", message: t("telegram.mapSent") });
                setMapPickerOpen(false);
            } catch (e) {
                addToast({
                    type: "error",
                    message: toUserFacingApiError(e, t("telegram.sendMapFailed")),
                });
            } finally {
                setMediaBusy(null);
            }
            return;
        }

        if (!stcmBaseUrl || !savedStcmRel) {
            addToast({
                type: "error",
                message: t("telegram.chooseSavedMap"),
            });
            return;
        }

        setMediaBusy("map");
        try {
            const previewUrl = getStcmPreviewBmpUrl(stcmBaseUrl, savedStcmRel);
            const res = await authedFetch(previewUrl, { mode: "cors", cache: "no-store" });
            if (!res.ok) {
                throw new Error("Couldn't load the map preview. Try again.");
            }
            const blob = await res.blob();
            const label = stcmDisplayName(savedStcmRel);
            const filename = `${label.replace(/[^\w.-]+/g, "_") || "map"}-${Date.now()}.bmp`;
            await sendTelegramDocument(token, chat, blob, {
                filename,
                caption: `Saved map: ${label}`,
            });
            addToast({ type: "success", message: t("telegram.savedMapSent") });
            setMapPickerOpen(false);
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("telegram.sendSavedMapFailed")),
            });
        } finally {
            setMediaBusy(null);
        }
    }, [addToast, botToken, chatId, mapImageUrl, mapSource, savedStcmRel, stcmBaseUrl, t]);

    const handleSendGalleryFile = useCallback(
        async (name: string, kind: GalleryKind) => {
            const token = botToken.trim();
            const chat = chatId.trim();
            if (!token || !chat) {
                addToast({
                    type: "error",
                    message: t("telegram.enterCredentials"),
                });
                return;
            }
            if (!recorderBaseUrl) {
                addToast({ type: "error", message: t("telegram.recorderNotConfigured") });
                return;
            }

            if (kind === "photo") setMediaBusy("photo");
            else setMediaBusy("video");

            try {
                const url = getRecordingFileUrl(recorderBaseUrl, name);
                const res = await authedFetch(url, { mode: "cors", cache: "no-store" });
                if (!res.ok) {
                    throw new Error("Couldn't download the file. Try again.");
                }
                const blob = await res.blob();
                if (kind === "photo") {
                    await sendTelegramPhoto(token, chat, blob, {
                        filename: name,
                        caption: name,
                    });
                    addToast({ type: "success", message: t("telegram.photoSent") });
                } else {
                    await sendTelegramVideo(token, chat, blob, {
                        filename: name,
                        caption: name,
                    });
                    addToast({ type: "success", message: t("telegram.videoSent") });
                }
                setGalleryKind(null);
            } catch (e) {
                addToast({
                    type: "error",
                    message: toUserFacingApiError(e, t("telegram.sendFileFailed")),
                });
            } finally {
                setMediaBusy(null);
            }
        },
        [addToast, botToken, chatId, recorderBaseUrl, t]
    );

    if (!hydrated) {
        return (
            <div className="flex flex-col">
                <div className="shrink-0 border-b border-black80 pb-4">
                    <p className="text-h4 font-semibold text-white">{t("telegram.title")}</p>
                </div>
                <p className="mt-4 text-h5 text-black40">{t("common:loading")}</p>
            </div>
        );
    }

    return (
        <div className="flex min-w-0 flex-col">
            <BottomSheet
                visible={galleryKind !== null}
                onClose={() => !anyBusy && setGalleryKind(null)}
                className="mx-auto flex w-full max-w-2xl flex-col border border-black80 bg-black100 shadow-lg"
                zIndexClassName="z-[120]"
            >
                {galleryKind ? (
                    <>
                        <div className="flex shrink-0 items-center justify-between border-b border-black80 px-3 py-2">
                            <p className="text-h5 font-semibold text-white">
                                {galleryKind === "photo" ? t("telegram.choosePhoto") : t("telegram.chooseVideo")}
                            </p>
                            <CloseButton
                                className="relative shrink-0"
                                onClick={() => !anyBusy && setGalleryKind(null)}
                            />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
                            {!recorderBaseUrl ? (
                                <p className="text-h5 text-black60">
                                    {t("telegram.recorderNotConfigured")}
                                </p>
                            ) : galleryLoading ? (
                                <p className="text-h5 text-black60">{t("telegram.loadingGallery")}</p>
                            ) : galleryError ? (
                                <p className="text-h5 text-black40">{galleryError}</p>
                            ) : filteredGalleryFiles.length === 0 ? (
                                <p className="text-h5 text-black60">
                                    {galleryKind === "photo" ? t("telegram.noPhotos") : t("telegram.noVideos")}
                                </p>
                            ) : (
                                <>
                                    <div className="shrink-0 overflow-hidden rounded-r5 border border-black80 bg-black90">
                                        <p className="border-b border-black80 px-2 py-1.5 text-h5 text-black60">
                                            {t("telegram.preview")}
                                        </p>
                                        <div className="flex min-h-[12rem] items-center justify-center bg-black90 p-2">
                                            {gallerySelectedUrl && galleryKind === "photo" ? (
                                                <img
                                                    src={gallerySelectedUrl}
                                                    alt=""
                                                    className="mx-auto max-h-56 w-auto max-w-full object-contain"
                                                />
                                            ) : gallerySelectedUrl && galleryKind === "video" ? (
                                                <video
                                                    key={gallerySelectedUrl}
                                                    src={gallerySelectedUrl}
                                                    controls
                                                    muted
                                                    playsInline
                                                    className="mx-auto max-h-56 w-full max-w-full object-contain"
                                                />
                                            ) : (
                                                <p className="text-h5 text-black60">{t("telegram.selectFile")}</p>
                                            )}
                                        </div>
                                        {gallerySelectedName ? (
                                            <p className="truncate border-t border-black80 px-2 py-1.5 text-h5 text-white">
                                                {gallerySelectedName}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div className="min-h-0 max-h-[38vh] flex-1 overflow-y-auto overscroll-y-contain pr-1">
                                        <ul className="space-y-1.5 whitespace-normal">
                                            {filteredGalleryFiles.map((f) => {
                                                const thumbUrl = getRecordingFileUrl(
                                                    recorderBaseUrl,
                                                    f.name
                                                );
                                                const sel = gallerySelectedName === f.name;
                                                return (
                                                    <li key={f.name}>
                                                        <button
                                                            type="button"
                                                            disabled={anyBusy}
                                                            onClick={() => setGallerySelectedName(f.name)}
                                                            className={`flex w-full items-center gap-2 rounded-r5 border px-2 py-1.5 text-left transition-colors disabled:opacity-50 ${
                                                                sel
                                                                    ? "border-primary2 bg-black80"
                                                                    : "border-black80 bg-black90 hover:bg-black80"
                                                            }`}
                                                        >
                                                            {galleryKind === "photo" ? (
                                                                <img
                                                                    src={thumbUrl}
                                                                    alt=""
                                                                    className="h-14 w-14 shrink-0 rounded object-cover"
                                                                />
                                                            ) : (
                                                                <video
                                                                    src={thumbUrl}
                                                                    muted
                                                                    playsInline
                                                                    preload="metadata"
                                                                    className="h-14 w-20 shrink-0 rounded bg-black object-cover"
                                                                />
                                                            )}
                                                            <span className="min-w-0 flex-1 truncate text-h5 text-white">
                                                                {f.name}
                                                            </span>
                                                            <span className="shrink-0 text-h5 text-black60">
                                                                {formatBytes(f.size_bytes)}
                                                            </span>
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                    <FunctionalButton
                                        className="shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2.5 hover:bg-primary1 disabled:opacity-50"
                                        title={
                                            anyBusy
                                                ? t("common:sending")
                                                : gallerySelectedName
                                                  ? t("telegram.sendSelection")
                                                  : t("telegram.selectFile")
                                        }
                                        titleClassName="!normal-case text-h5 font-semibold text-white"
                                        disabled={
                                            !credentialsReady ||
                                            anyBusy ||
                                            !gallerySelectedName ||
                                            !galleryKind
                                        }
                                        onClick={() => {
                                            if (
                                                gallerySelectedName &&
                                                galleryKind
                                            ) {
                                                void handleSendGalleryFile(
                                                    gallerySelectedName,
                                                    galleryKind
                                                );
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </>
                ) : null}
            </BottomSheet>

            <BottomSheet
                visible={mapPickerOpen}
                onClose={() => !anyBusy && setMapPickerOpen(false)}
                className="mx-auto flex w-full max-w-2xl flex-col border border-black80 bg-black100 shadow-lg"
                zIndexClassName="z-[120]"
            >
                        <div className="flex shrink-0 items-center justify-between border-b border-black80 px-3 py-2">
                            <p className="text-h5 font-semibold text-white">{t("telegram.chooseMap")}</p>
                            <CloseButton
                                className="relative shrink-0"
                                onClick={() => !anyBusy && setMapPickerOpen(false)}
                            />
                        </div>
                        <div className="flex min-h-0 flex-1 flex-col gap-3 p-3">
                            <div className="shrink-0 overflow-hidden rounded-r5 border border-black80 bg-black90">
                                <p className="border-b border-black80 px-2 py-1.5 text-h5 text-black60">
                                    Preview
                                </p>
                                <div className="flex min-h-[12rem] items-center justify-center bg-black90 p-2">
                                    {showLiveMapPreview && mapImageUrl ? (
                                        <img
                                            src={mapImageUrl}
                                            alt=""
                                            className="mx-auto max-h-56 w-auto max-w-full object-contain"
                                        />
                                    ) : showSavedMapPreview && savedMapPreviewUrl ? (
                                        <img
                                            src={savedMapPreviewUrl}
                                            alt=""
                                            className="mx-auto max-h-56 w-auto max-w-full object-contain bg-black70"
                                        />
                                    ) : (
                                        <p className="text-h5 text-black60">
                                            {t("telegram.selectLiveOrSaved")}
                                        </p>
                                    )}
                                </div>
                                <p className="truncate border-t border-black80 px-2 py-1.5 text-h5 text-white">
                                    {mapSource === "live"
                                        ? mapImageUrl
                                            ? t("telegram.currentLiveMap")
                                            : t("telegram.liveMapNotStreaming")
                                        : savedStcmRel
                                          ? t("telegram.savedMapLabel", { name: stcmDisplayName(savedStcmRel) })
                                          : t("telegram.savedMapNone")}
                                </p>
                            </div>

                            <div className="min-h-0 max-h-[38vh] flex-1 overflow-y-auto overscroll-y-contain pr-1">
                                <ul className="space-y-1.5 whitespace-normal">
                                    <li>
                                        <button
                                            type="button"
                                            disabled={anyBusy}
                                            onClick={() => setMapSource("live")}
                                            className={`flex w-full items-center gap-2 rounded-r5 border px-2 py-1.5 text-left transition-colors disabled:opacity-50 ${
                                                mapSource === "live"
                                                    ? "border-primary2 bg-black80"
                                                    : "border-black80 bg-black90 hover:bg-black80"
                                            }`}
                                        >
                                            {mapImageUrl ? (
                                                <img
                                                    src={mapImageUrl}
                                                    alt=""
                                                    className="h-14 w-14 shrink-0 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-black70 text-h5 text-black60">
                                                    —
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="text-h5 text-white">{t("telegram.currentLiveMap")}</p>
                                                {!mapImageUrl ? (
                                                    <p className="text-h5 text-black60">
                                                        {t("telegram.waitingForMap")}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </button>
                                    </li>

                                    <li className="px-1 pt-1">
                                        <p className="text-h5 font-semibold text-black60">
                                            {t("telegram.savedMapsHeading")}
                                        </p>
                                    </li>

                                    {!stcmBaseUrl ? (
                                        <li className="px-1">
                                            <p className="text-h5 text-black60">
                                                {t("telegram.stcmNotConfigured")}
                                            </p>
                                        </li>
                                    ) : stcmListLoading ? (
                                        <li className="px-1">
                                            <p className="text-h5 text-black60">{t("telegram.loadingSavedMaps")}</p>
                                        </li>
                                    ) : stcmListError ? (
                                        <li className="px-1">
                                            <p className="text-h5 text-black40">{stcmListError}</p>
                                        </li>
                                    ) : stcmFiles.length === 0 ? (
                                        <li className="px-1">
                                            <p className="text-h5 text-black60">
                                                {t("telegram.noSavedMaps")}
                                            </p>
                                        </li>
                                    ) : (
                                        stcmFiles.map((f) => {
                                            const thumbUrl =
                                                stcmBaseUrl != null
                                                    ? getStcmPreviewBmpUrl(stcmBaseUrl, f)
                                                    : "";
                                            const sel =
                                                mapSource === "saved" && savedStcmRel === f;
                                            return (
                                                <li key={f}>
                                                    <button
                                                        type="button"
                                                        disabled={anyBusy}
                                                        onClick={() => {
                                                            setMapSource("saved");
                                                            setSavedStcmRel(f);
                                                        }}
                                                        className={`flex w-full items-center gap-2 rounded-r5 border px-2 py-1.5 text-left transition-colors disabled:opacity-50 ${
                                                            sel
                                                                ? "border-primary2 bg-black80"
                                                                : "border-black80 bg-black90 hover:bg-black80"
                                                        }`}
                                                    >
                                                        {stcmBaseUrl ? (
                                                            <img
                                                                src={thumbUrl}
                                                                alt=""
                                                                className="h-14 w-14 shrink-0 rounded object-cover bg-black70"
                                                            />
                                                        ) : null}
                                                        <span className="min-w-0 flex-1 truncate text-h5 text-white">
                                                            {stcmDisplayName(f)}
                                                        </span>
                                                    </button>
                                                </li>
                                            );
                                        })
                                    )}
                                </ul>
                            </div>

                            <FunctionalButton
                                className="shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2.5 hover:bg-primary1 disabled:opacity-50"
                                title={
                                    mediaBusy === "map"
                                        ? t("common:sending")
                                        : canSendMap
                                          ? t("telegram.sendSelection")
                                          : t("telegram.selectAvailableMap")
                                }
                                titleClassName="!normal-case text-h5 font-semibold text-white"
                                disabled={!credentialsReady || anyBusy || !canSendMap}
                                onClick={() => void handleSendMap()}
                            />
                        </div>
            </BottomSheet>

            <div className="shrink-0 border-b border-black80 pb-4">
                <p className="text-h4 font-semibold text-white">{t("telegram.title")}</p>
                <p className="mt-1 text-h5 text-black40">
                    <Trans
                        i18nKey="telegram.description"
                        ns="settings"
                        values={{ bot: NOTIFICATION_BOT_USERNAME }}
                        components={{ 1: <span className="text-white" /> }}
                    />
                </p>
            </div>

            <div className="mt-4 flex flex-col gap-4 rounded-r10 border border-black80 bg-black100 p-4">
                <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-h5 font-semibold text-white">{t("telegram.enableNotifications")}</p>
                        <p className="mt-0.5 text-h5 text-black60">
                            {t("telegram.enableDescription")}
                        </p>
                    </div>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={enabled}
                        onClick={() => setEnabled((v) => !v)}
                        className={`relative h-8 w-14 shrink-0 rounded-full border transition-colors ${
                            enabled
                                ? "border-primary2 bg-primary2"
                                : "border-black60 bg-black70"
                        }`}
                    >
                        <span
                            className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                                enabled ? "translate-x-6" : "translate-x-0"
                            }`}
                        />
                    </button>
                </div>

                {enabled ? (
                    <>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="telegram-bot-token" className="text-h5 text-white">
                                {t("telegram.botToken")}
                            </label>
                            <input
                                id="telegram-bot-token"
                                type="password"
                                autoComplete="off"
                                value={botToken}
                                onChange={(e) => setBotToken(e.target.value)}
                                placeholder={t("telegram.botTokenPlaceholder")}
                                className="w-full rounded-r5 border border-black80 bg-black70 px-3 py-2.5 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none"
                            />
                            <p className="text-h5 text-black60">
                                {t("telegram.botTokenHint")}
                            </p>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="telegram-chat-id" className="text-h5 text-white">
                                {t("telegram.chatId")}
                            </label>
                            <input
                                id="telegram-chat-id"
                                type="text"
                                autoComplete="off"
                                value={chatId}
                                onChange={(e) => setChatId(e.target.value)}
                                placeholder={t("telegram.chatIdPlaceholder")}
                                className="w-full rounded-r5 border border-black80 bg-black70 px-3 py-2.5 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none"
                            />
                            <p className="text-h5 text-black60">
                                {t("telegram.chatIdHint")}
                            </p>
                            <div className="mt-2 rounded-r5 border border-black80 bg-black90 p-3">
                                <p className="text-h5 font-semibold text-white">
                                    {t("telegram.beforeReceiveTitle")}
                                </p>
                                <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-h5 text-black40">
                                    <li>
                                        <Trans
                                            i18nKey="telegram.beforeReceiveStep1"
                                            ns="settings"
                                            values={{ bot: NOTIFICATION_BOT_USERNAME }}
                                            components={{
                                                1: (
                                                    <a
                                                        href={NOTIFICATION_BOT_TELEGRAM_URL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-primary2 underline hover:text-primary1"
                                                    />
                                                ),
                                            }}
                                        />
                                    </li>
                                    <li>
                                        <Trans
                                            i18nKey="telegram.beforeReceiveStep2"
                                            ns="settings"
                                            components={{
                                                1: <span className="text-white" />,
                                                2: <span className="font-mono text-white" />,
                                            }}
                                        />
                                    </li>
                                    <li>
                                        {t("telegram.beforeReceiveStep3", { bot: NOTIFICATION_BOT_USERNAME })}
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </>
                ) : null}

                <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80 disabled:opacity-50"
                        title={t("common:test")}
                        titleClassName="!normal-case text-h5 font-semibold text-white"
                        disabled={anyBusy}
                        onClick={() => void handleTest()}
                    />
                    <FunctionalButton
                        className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 hover:bg-primary1"
                        title={t("common:save")}
                        titleClassName="!normal-case text-h5 font-semibold text-white"
                        onClick={handleSave}
                    />
                </div>
            </div>

            <div className="mt-6 shrink-0 border-b border-black80 pb-3">
                <p className="text-h4 font-semibold text-white">{t("telegram.sendToChat")}</p>
                <p className="mt-1 text-h5 text-black40">
                    {t("telegram.sendToChatDescription")}
                </p>
            </div>

            <div className="mt-3 flex flex-col gap-3 rounded-r10 border border-black80 bg-black100 p-4">
                <div>
                    <p className="text-h5 font-semibold text-white">{t("telegram.mapsAndGallery")}</p>
                    <p className="mt-1 text-h5 text-black60">
                        {t("telegram.mapsAndGalleryDescription", {
                            status: recorderBaseUrl
                                ? t("telegram.recorderConnected")
                                : t("telegram.recorderNotConfiguredShort"),
                        })}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80 disabled:opacity-50"
                        title={t("telegram.chooseMapToSend")}
                        titleClassName="!normal-case text-h5 font-semibold text-white"
                        disabled={!credentialsReady || anyBusy}
                        onClick={() => setMapPickerOpen(true)}
                    />
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80 disabled:opacity-50"
                        title={
                            mediaBusy === "photo"
                                ? t("telegram.sendingPhoto")
                                : t("telegram.sendPhotoFromGallery")
                        }
                        titleClassName="!normal-case text-h5 font-semibold text-white"
                        disabled={!credentialsReady || anyBusy}
                        onClick={() => setGalleryKind("photo")}
                    />
                    <FunctionalButton
                        className="rounded-r5 border border-black80 bg-black70 px-4 py-2 hover:bg-black80 disabled:opacity-50"
                        title={
                            mediaBusy === "video"
                                ? t("telegram.sendingVideo")
                                : t("telegram.sendVideoFromGallery")
                        }
                        titleClassName="!normal-case text-h5 font-semibold text-white"
                        disabled={!credentialsReady || anyBusy}
                        onClick={() => setGalleryKind("video")}
                    />
                </div>
            </div>

            <div className="mt-6 shrink-0 border-b border-black80 pb-3">
                <p className="text-h4 font-semibold text-white">{t("telegram.notificationSelection")}</p>
                <p className="mt-1 text-h5 text-black40">
                    {t("telegram.notificationSelectionDescription")}
                </p>
            </div>

            <div className="mt-3 flex flex-col gap-3 rounded-r10 border border-black80 bg-black100 p-4">
                <div className="flex items-center justify-between gap-4">
                    <p className="text-h5 font-semibold text-white capitalize">{t("telegram.person")}</p>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={notifyPerson}
                        aria-label={t("telegram.personNotificationsAria")}
                        onClick={() => setNotifyPerson((v) => !v)}
                        className={`relative h-8 w-14 shrink-0 rounded-full border transition-colors ${
                            notifyPerson
                                ? "border-primary2 bg-primary2"
                                : "border-black60 bg-black70"
                        }`}
                    >
                        <span
                            className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                                notifyPerson ? "translate-x-6" : "translate-x-0"
                            }`}
                        />
                    </button>
                </div>
                <div className="flex items-center justify-between gap-4">
                    <p className="text-h5 font-semibold text-white capitalize">{t("telegram.fire")}</p>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={notifyFire}
                        aria-label={t("telegram.fireNotificationsAria")}
                        onClick={() => setNotifyFire((v) => !v)}
                        className={`relative h-8 w-14 shrink-0 rounded-full border transition-colors ${
                            notifyFire
                                ? "border-primary2 bg-primary2"
                                : "border-black60 bg-black70"
                        }`}
                    >
                        <span
                            className={`absolute top-1 left-1 h-6 w-6 rounded-full bg-white shadow transition-transform ${
                                notifyFire ? "translate-x-6" : "translate-x-0"
                            }`}
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TelegramNotificationSettingsPanel;
